/*
 * A deliberately small, dependency-free mobile purchase affordance.
 *
 * The storefront keeps one real product form. This control only mirrors the
 * visible add-to-cart label, price and disabled state, then delegates a click
 * to that real form. It never duplicates variant state or submits its own
 * request, which keeps app-powered product forms and analytics intact.
 */
(() => {
  const mobileQuery = window.matchMedia('(max-width: 768px)');

  const initializeBar = (bar) => {
    if (bar.dataset.fsPurchaseBarInitialized === 'true') return;
    const form = document.getElementById(bar.dataset.fsFormId);
    if (!form) return;

    const sourceButton = form.querySelector('[data-add-to-cart]');
    const proxyButton = bar.querySelector('[data-fs-mobile-purchase-proxy]');
    const label = bar.querySelector('[data-fs-mobile-purchase-label]');
    const price = bar.querySelector('[data-fs-mobile-purchase-price]');
    const productScope = bar.closest('.product-single__meta') || document;

    if (!sourceButton || !proxyButton || !label || !price) return;

    bar.dataset.fsPurchaseBarInitialized = 'true';

    const sourceRect = sourceButton.getBoundingClientRect();
    let sourceIsVisible = sourceRect.bottom > 0 && sourceRect.top < window.innerHeight;

    const privacyIsVisible = () => {
      const banner = document.querySelector('#shopify-pc__banner, .shopify-pc__banner');
      if (!banner) return false;
      const rect = banner.getBoundingClientRect();
      const style = window.getComputedStyle(banner);
      return style.display !== 'none' && style.visibility !== 'hidden' && rect.width > 0 && rect.height > 0;
    };

    const currentPriceText = () => {
      const priceElements = Array.from(productScope.querySelectorAll('.product-block--price [data-product-price]'));
      const priceElement = priceElements.find((element) => {
        if (element.closest('.hide, [hidden]')) return false;
        const style = window.getComputedStyle(element);
        return style.display !== 'none' && style.visibility !== 'hidden' && element.getClientRects().length > 0;
      }) || priceElements[0];

      if (!priceElement) return '';

      // The price snippet outputs one visual value plus a visually-hidden copy
      // for screen readers. Reading the parent textContent concatenates both.
      const visualPrice = Array.from(priceElement.children).find((child) => (
        child.getAttribute('aria-hidden') === 'true' && !child.classList.contains('visually-hidden')
      ));

      if (visualPrice) return visualPrice.textContent.trim();

      const textOnlyClone = priceElement.cloneNode(true);
      textOnlyClone.querySelectorAll('.visually-hidden, [hidden], .hide').forEach((element) => element.remove());
      return textOnlyClone.textContent.trim();
    };

    const sync = () => {
      const sourceLabel = sourceButton.querySelector('[data-add-to-cart-text]');
      const isDisabled = sourceButton.disabled || sourceButton.getAttribute('aria-disabled') === 'true';

      label.textContent = (sourceLabel?.textContent || sourceButton.textContent || 'Add to cart').trim();
      proxyButton.disabled = isDisabled;
      price.textContent = currentPriceText() || price.textContent.trim();

      const hasPrivacyOverlay = privacyIsVisible();
      document.documentElement.classList.toggle('fs-privacy-visible', hasPrivacyOverlay);
      const shouldShow = mobileQuery.matches && !sourceIsVisible && !hasPrivacyOverlay;
      bar.classList.toggle('is-visible', shouldShow);
      bar.setAttribute('aria-hidden', String(!shouldShow));
      bar.toggleAttribute('inert', !shouldShow);
      proxyButton.tabIndex = shouldShow && !isDisabled ? 0 : -1;
    };

    const observer = new IntersectionObserver((entries) => {
      sourceIsVisible = entries[0]?.isIntersecting ?? true;
      sync();
    }, { threshold: 0.12 });

    observer.observe(sourceButton);
    mobileQuery.addEventListener('change', sync);

    const bindPrivacyBanner = () => {
      const banner = document.querySelector('#shopify-pc__banner, .shopify-pc__banner');
      if (!banner || banner.dataset.fsPurchaseBarObserved === 'true') return false;
      banner.dataset.fsPurchaseBarObserved = 'true';
      new MutationObserver(sync).observe(banner, {
        attributes: true,
        attributeFilter: ['class', 'hidden', 'style'],
        childList: true,
        subtree: true
      });
      sync();
      return true;
    };

    if (!bindPrivacyBanner()) {
      const bodyObserver = new MutationObserver(() => {
        if (bindPrivacyBanner()) bodyObserver.disconnect();
      });
      if (document.body) {
        bodyObserver.observe(document.body, { childList: true });
      }
      window.setTimeout(() => bodyObserver.disconnect(), 8000);
    }

    new MutationObserver(sync).observe(sourceButton, {
      attributes: true,
      attributeFilter: ['disabled', 'aria-disabled'],
      childList: true,
      characterData: true,
      subtree: true
    });

    const priceBlock = productScope.querySelector('.product-block--price');
    if (priceBlock) {
      new MutationObserver(sync).observe(priceBlock, {
        childList: true,
        characterData: true,
        subtree: true
      });
    }

    proxyButton.addEventListener('click', () => {
      sync();
      if (!sourceButton.disabled) sourceButton.click();
    });

    sync();
  };

  const initialize = () => document
    .querySelectorAll('[data-fs-mobile-purchase-bar]')
    .forEach(initializeBar);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();

